import type { BoxProps, InputElementProps } from '@chakra-ui/react';
import {
  Input as ChakraInput,
  InputAddon as ChakraInputAddon,
  InputProps as ChakraInputProps,
  Group,
  InputAddonProps,
  InputElement,
} from '@chakra-ui/react';
import React from 'react';

type InputSize = 'sm' | 'md';

export type InputProps = Omit<ChakraInputProps, 'size'> & {
  size?: InputSize;
};

const sizeProps: Record<InputSize, InputProps> = {
  sm: { h: 8 },
  md: { h: 10 },
};

const Input = React.forwardRef<HTMLInputElement, InputProps>(
  ({ size = 'md', ...rest }, ref) => (
    <ChakraInput
      background="white"
      border="1px solid"
      borderColor="gray.400"
      focusRing="mixed"
      focusRingColor="blue.500"
      focusRingWidth="1px"
      paddingLeft={2}
      _hover={{ borderColor: 'gray.500' }}
      _invalid={{
        backgroundColor: 'red.50',
        borderColor: 'red.400',
        focusRingColor: 'red.400',
        _hover: { borderColor: 'red.500' },
      }}
      _disabled={{
        borderColor: 'gray.400 !important',
        background: 'gray.50',
        color: 'gray.400',
        opacity: 1,
      }}
      ref={ref}
      size={size}
      {...sizeProps[size]}
      {...rest}
    />
  )
);

export const InputAddon = React.forwardRef<HTMLDivElement, InputAddonProps>(
  (props, ref) => (
    <ChakraInputAddon borderColor="gray.400" ref={ref} {...props} />
  )
);

export interface InputGroupProps extends BoxProps {
  startElementProps?: InputElementProps;
  endElementProps?: InputElementProps;
  startElement?: React.ReactNode;
  endElement?: React.ReactNode;
  children: React.ReactElement<InputElementProps>;
  startOffset?: InputElementProps['paddingStart'];
  endOffset?: InputElementProps['paddingEnd'];
}

export const InputGroup = React.forwardRef<HTMLDivElement, InputGroupProps>(
  function InputGroup(props, ref) {
    const {
      startElement,
      startElementProps,
      endElement,
      endElementProps,
      children,
      startOffset = '6px',
      endOffset = '6px',
      ...rest
    } = props;

    const child =
      React.Children.only<React.ReactElement<InputElementProps>>(children);

    return (
      <Group ref={ref} w="full" {...rest}>
        {startElement && (
          <InputElement
            color="gray.500"
            pointerEvents="none"
            {...startElementProps}
          >
            {startElement}
          </InputElement>
        )}
        {React.cloneElement(child, {
          ...(startElement && {
            ps: `calc(var(--input-height) - ${startOffset})`,
          }),
          ...(endElement && { pe: `calc(var(--input-height) - ${endOffset})` }),
          ...children.props,
        })}
        {endElement && (
          <InputElement color="gray.500" placement="end" {...endElementProps}>
            {endElement}
          </InputElement>
        )}
      </Group>
    );
  }
);

export default Input;
